<template>
    <div>
        <div v-for="(doc, index) in localApp.documents" :key="index" class="document-item border p-3 mb-2 rounded shadow-sm bg-white">
            <ValidationObserver :ref="'docObserver' + index">
                <div class="d-flex justify-content-between align-items-center mb-2">
                    <span class="font-weight-bold">{{ getLocaleText(doc, 'document_type_name') }}</span>
                    <b-button size="sm" variant="outline-primary" @click="previewDocument(doc)">
                        <i class="ri-eye-line mr-1"></i> {{ $t('show') }}
                    </b-button>
                </div>
                <b-row>
                    <b-col sm="6">
                        <ValidationProvider :name="doc.document_name+'-'+index" rules="required" v-slot="{ errors }">
                            <b-form-group :label="$t('status')">
                                <ParameterSelectbox
                                    code="special_student_application_document_status"
                                    v-model="doc.status"
                                    :multiple="false"
                                    :disabled="!isActiveRole"
                                    :validate-error="errors[0]"
                                />
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                    <b-col sm="6" v-if="doc.status === 'rejected'">
                        <ValidationProvider :name="doc.document_name+'-'+index"  rules="required" v-slot="{ errors }">
                            <b-form-group :label="$t('rejection_reason')">
                                <b-form-textarea
                                    rows="8"
                                    v-model="doc.description"
                                    :disabled="!isActiveRole"
                                    :placeholder="$t('write_reason_for_rejection')"
                                    :state="errors[0] ? false : null"
                                ></b-form-textarea>
                                <b-form-invalid-feedback>{{ errors[0] }}</b-form-invalid-feedback>
                            </b-form-group>
                        </ValidationProvider>
                    </b-col>
                </b-row>
                <div class="d-flex justify-content-center align-items-center mt-2">
                    <ProcessingButton
                        :processing="evaluateDocumentsLoading"
                        :disabled="!isActiveRole"
                        @click="evaluateSingleDocument(doc, index)"
                        :label="$t('save')"
                    />
                </div>
            </ValidationObserver>
        </div>

        <b-modal ref="documentPreviewModal" size="xl" hide-footer centered :title="previewTitle">
            <div v-if="previewData" class="d-flex justify-content-center align-items-center bg-light p-3 rounded" style="min-height: 400px; max-height: 80vh; overflow-y: auto;">
                 <template v-if="previewData.preview_type === 'image'">
                     <img :src="previewData.file_url" class="img-fluid rounded shadow-sm" style="max-height: 75vh;" alt="Document Preview" />
                 </template>
                 <template v-else-if="previewData.preview_type === 'pdf'">
                     <embed :src="previewData.file_url" type="application/pdf" width="100%" height="700px" />
                 </template>
                 <template v-else>
                     <div class="text-center">
                         <i class="ri-file-warning-line text-warning" style="font-size: 3rem;"></i>
                         <p class="mt-2 text-muted">{{ $t('preview_not_supported') || 'Bu dosya tipi için önizleme desteklenmiyor.' }}</p>
                         <b-button variant="primary" :href="previewData.file_url" target="_blank" class="mt-2">
                             <i class="ri-download-2-line"></i> {{ $t('download') }}
                         </b-button>
                     </div>
                 </template>
            </div>
        </b-modal>
    </div>
</template>

<script>
import SpecialStudentApplicationService from "@/services/SpecialStudentApplicationService";
import SpecialStudentApplicationRegisterServices from "@/services/SpecialStudentApplicationRegisterServices";
import ParameterSelectbox from "@/components/interactive-fields/ParameterSelectbox.vue";
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import ProcessingButton from "@/components/elements/ProcessingButton.vue";
export default {
    name: "DocumentApprovalTab",
    components: {
        ProcessingButton,
        ParameterSelectbox,
        ValidationObserver,
        ValidationProvider
    },
    props: {
        localApp: {
            type: Object,
            required: true
        },
        isActiveRole: {
            type: Boolean,
            required: true
        }
    },
    data() {
        return {
            evaluateDocumentsLoading: false,
            previewData: null,
            previewTitle: ''
        }
    },
    methods: {
        previewDocument(doc){
            const data = {
                document_type: doc.document_type
            };
            this.evaluateDocumentsLoading = true;
            SpecialStudentApplicationRegisterServices.previewDocument(doc.id,data)
                .then(response => {
                    if (response.data && response.data.success && response.data.data) {
                        this.previewData = response.data.data;
                        this.previewTitle = this.getLocaleText(doc, 'document_type_name');
                        this.$refs.documentPreviewModal.show();
                    } else {
                        this.$toast.error('Önizleme verisi alınamadı.');
                    }
                })
                .catch(error => {
                    this.showErrors(error, null, true);
                })
                .finally(() => {
                    this.evaluateDocumentsLoading = false;
                });
        },
        downloadDocument(doc) {
            const data = {
                document_type: doc.document_type
            };
            SpecialStudentApplicationRegisterServices.showDocuments(doc.id, data)
                .then(response => {
                    this._downloadFile(response, this.getLocaleText(doc, 'document_type_name') + '.' + (response.headers.file_extension ?? 'pdf'));
                })
                .catch(error => {
                    this.showErrors(error, null, true);
                });
        },

        async evaluateSingleDocument(doc, index) {
            const observer = this.$refs['docObserver' + index][0];
            const isValid = await observer.validate();
            if (!isValid) return;

            this.$swal
                .fire({
                    text: this.$t('are_you_sure_to_approve'),
                    showCancelButton: true,
                    confirmButtonText: this.$t('yes'),
                    cancelButtonText: this.$t('no'),
                })
                .then(async (result) => {
                    if (result.isConfirmed) {
                        this.evaluateDocumentsLoading = true;
                        const payload = {
                            document_id: doc.id,
                            status: doc.status,
                            description: doc.description
                        };
                        if (doc.status === 'approved') {
                            delete payload.description;
                        }

                        SpecialStudentApplicationService.evaluateDocument(doc.id, payload)
                            .then(response => {
                                this.$emit('update-application', response.data.data);
                                this.$toast.success(this.$t('proccess_success'));
                            })
                            .catch(err => {
                                this.showErrors(err, observer);
                            })
                            .finally(() => {
                                this.evaluateDocumentsLoading = false;
                            });
                    }
                });
        }
    }
}
</script>

<style scoped>
.document-item {
    transition: all 0.2s ease-in-out;
}
.document-item:hover {
    background-color: #f8f9fa !important;
}
</style>
